import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { resolvePostLoginRoute } from '@/utils/setupStatus';
import { useAuth } from '@/context/AuthContext';
import { getApiEndpoint } from '@/utils/ERPUtils';
import site_logo from '@/assets/site_logo.jpg';
import { BRAND_NAME, getCachedBrandLogo, setCachedBrandLogo } from '@/utils/branding';

import { logger } from '@/utils/logger';
const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [brandLogo, setBrandLogo] = useState<string | null>(getCachedBrandLogo);
  const [brandName, setBrandName] = useState<string>(BRAND_NAME);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, signIn, loading: authLoading } = useAuth();
  const hasNavigatedRef = React.useRef(false);

  // Redirect if already logged in (prevent double navigation).
  // Wait for the initial session check to finish (authLoading) so we never
  // flash the form before knowing whether a session already exists.
  useEffect(() => {
    if (!authLoading && user && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      // Admins on an unconfigured workspace land on Customise first.
      resolvePostLoginRoute().then((route) => navigate(route, { replace: true }));
    }
  }, [authLoading, user, navigate]);

  // Fetch company branding (logo + name) for pre-auth display.
  // Falls back to bundled site_logo if API is unavailable
  // or no Company record has been created yet.
  useEffect(() => {
    let mounted = true;
    axios
      .get(getApiEndpoint('GET_DEFAULT_BRANDING'))
      .then((res) => {
        if (!mounted) return;
        const data = res?.data?.message?.data;
        // Fall back to the bundled logo when no company logo is set.
        const url = data?.logo_url || null;
        setBrandLogo(url);
        setCachedBrandLogo(url); // keep the reload cache in sync
        if (data?.company_name) setBrandName(data.company_name);
      })
      .catch((err) => {
        logger.error('Branding fetch failed:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!email.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    if (!password.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter your password.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await signIn(email.trim(), password);

      if (error) {
        // Handle different error types with specific messages
        let errorMessage = "Invalid email or password.";

        switch (error.type) {
          case 'network_error':
            errorMessage = "Network error. Please check your connection and try again.";
            break;
          case 'login_failed':
            errorMessage = error.message || "Login failed. Please check your credentials.";
            break;
          case 'user_fetch_error':
            errorMessage = "Login successful but failed to load user data. Please try again.";
            break;
          default:
            errorMessage = error.message || "An error occurred during login.";
        }

        toast({
          title: "Login Failed",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Welcome back!",
          description: "Login successful. Redirecting to dashboard...",
        });
        // Navigation will happen automatically via useEffect when user state updates
      }
    } catch (err) {
      logger.error('Login error:', err);
      toast({
        title: "Login Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }

    setLoading(false);
  };

  // While the initial session check runs - or while an already-authenticated
  // user is being redirected to the dashboard - show a loader instead of the
  // login form. This prevents the form from briefly flashing on /login.
  if (authLoading || user) {
    return (
      <div className="dark flex items-center justify-center h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-background text-foreground">
      {/* LEFT - boardroom panel */}
      <section className="relative overflow-hidden flex flex-col justify-between p-8 sm:p-12 lg:p-16 min-h-[360px]">
        {/* radial gradient overlays */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 20% 0%, hsl(var(--primary) / 0.10), transparent 55%), radial-gradient(ellipse at 80% 100%, hsl(var(--primary) / 0.08), transparent 50%)',
          }}
          aria-hidden="true"
        />

        {/* knowledge graph backdrop */}
        <svg
          className="absolute right-[-60px] top-1/2 -translate-y-1/2 w-[460px] h-[460px] opacity-30 hidden lg:block"
          viewBox="0 0 460 460"
          aria-hidden="true"
        >
          <g stroke="hsl(var(--primary))" strokeOpacity="0.4" strokeWidth="1">
            <line x1="230" y1="230" x2="100" y2="120" />
            <line x1="230" y1="230" x2="370" y2="100" />
            <line x1="230" y1="230" x2="380" y2="280" />
            <line x1="230" y1="230" x2="120" y2="340" />
            <line x1="230" y1="230" x2="260" y2="80" />
            <line x1="230" y1="230" x2="80" y2="240" />
            <line x1="230" y1="230" x2="320" y2="380" />
            <line x1="100" y1="120" x2="260" y2="80" />
            <line x1="370" y1="100" x2="260" y2="80" />
            <line x1="380" y1="280" x2="320" y2="380" />
            <line x1="120" y1="340" x2="80" y2="240" />
            <line x1="120" y1="340" x2="320" y2="380" />
            <line x1="370" y1="100" x2="380" y2="280" />
          </g>
          <circle cx="230" cy="230" r="9" fill="white" />
          <circle cx="100" cy="120" r="5" fill="hsl(var(--primary))" />
          <circle cx="370" cy="100" r="5" fill="hsl(var(--primary))" />
          <circle cx="380" cy="280" r="5" fill="hsl(var(--primary))" />
          <circle cx="120" cy="340" r="5" fill="hsl(var(--primary))" />
          <circle cx="260" cy="80" r="4" fill="hsl(var(--primary))" />
          <circle cx="80" cy="240" r="4" fill="hsl(var(--primary))" />
          <circle cx="320" cy="380" r="4" fill="hsl(var(--primary))" />
        </svg>

        {/* brand */}
        {/* No z-index: a stacking context here would trap the logo's
            mix-blend-mode and leave the inverted black box uncomposited.
            The header still paints above the backdrop via DOM order. */}
        <header className="relative flex items-center gap-3">
          <span className="inline-flex items-center">
            {/* Fade lives on the <img>, not this wrapper: an ancestor at
                fractional opacity creates a stacking context that traps the
                blend, flashing the uncomposited black box mid-fade. */}
            <img
              src={brandLogo || site_logo}
              alt={brandName}
              className="h-14 w-auto"
              style={{
                filter: 'invert(1)',
                mixBlendMode: 'screen',
                opacity: logoLoaded ? 1 : 0,
                transition: 'opacity 200ms ease',
              }}
              onLoad={() => setLogoLoaded(true)}
              onError={(e) => {
                // If the company-uploaded logo URL is broken, fall back to bundled default.
                const img = e.currentTarget as HTMLImageElement;
                if (img.src !== site_logo && !img.src.endsWith(site_logo)) {
                  img.src = site_logo;
                } else {
                  img.style.display = 'none';
                }
              }}
            />
          </span>
        </header>

        {/* body */}
        <div className="relative z-10 my-8 lg:my-0 max-w-[480px]">
          <span className="inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.08em] px-3 py-1.5 rounded-full border border-border text-muted-foreground mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px] shadow-primary" />
            Enterprise Intelligence
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight font-semibold mb-5">
            The brain of your{' '}
            <span className="bg-gradient-to-r from-primary to-white bg-clip-text text-transparent">
              company
            </span>
            , ready when you are.
          </h1>
          <p className="text-[17px] leading-relaxed text-muted-foreground max-w-[440px]">
            Connect every document, decision and dataset. Ask anything - your private RAG layer answers with citations from the sources your business already trusts.
          </p>
        </div>

        {/* stats - stacked on mobile: three columns inside the p-8 panel leaves
            ~98px each, which is narrower than "Provenance" renders at 22px, so
            the title overflowed its cell and wrapped, dropping its description
            out of line with the other two. Full width below sm keeps every
            title on one line and the descriptions aligned without tuning. */}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8 pt-8 border-t border-border">
          <div>
            <div className="text-[22px] font-semibold tracking-tight">Full Provenance</div>
            <div className="text-xs text-muted-foreground mt-1">Every answer traced to source</div>
          </div>
          <div>
            <div className="text-[22px] font-semibold tracking-tight">Dedicated</div>
            <div className="text-xs text-muted-foreground mt-1">Private instance per business</div>
          </div>
          <div>
            <div className="text-[22px] font-semibold tracking-tight">24/7</div>
            <div className="text-xs text-muted-foreground mt-1">Always-on intelligence</div>
          </div>
        </div>
      </section>

      {/* RIGHT - form panel */}
      <section className="relative flex items-center justify-center p-8 sm:p-12 lg:p-14 lg:border-l border-border">
        <div className="w-full max-w-sm">
          <h2 className="text-[28px] leading-tight tracking-tight font-semibold mb-2">
            Sign In
          </h2>
          <p className="text-[15px] text-muted-foreground mb-9">
            Enter your credentials to access {brandName}.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
                <Input
                  id="email"
                  type="email"
                  maxLength={254}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 relative h-11"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline underline-offset-4"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-7 text-xs text-muted-foreground text-center leading-relaxed">
            © 2026 {brandName}
            <span className="mx-2 opacity-50">·</span>
            Powered by{' '}
            <a
              href="https://ictlondon.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary border-b border-border hover:border-primary transition-colors"
            >
              ICT London
            </a>
          </p>
        </div>

        {/* System status */}
        <div className="absolute bottom-4 right-6 text-right text-[11px] uppercase tracking-[0.08em] text-muted-foreground/70">
          <div>System Status</div>
          <div className="flex items-center justify-end gap-1.5 mt-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]" />
            <span>Operational 99.9%</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
